import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, FormBuilder } from '@angular/forms';
import { GroupRule, Rule } from '../../../models/feature-flag.model';

/**
 * Component for building and managing advanced group rules
 * Allows combining multiple rules with AND/OR operators
 */
@Component({
  selector: 'cbre-group-rule',
  templateUrl: './group-rule.component.html',
  styleUrls: ['./group-rule.component.scss'],
  standalone: false,
})
export class GroupRuleComponent implements OnInit {
  /**
   * Parent form group
   */
  @Input() parentForm: FormGroup;

  /**
   * Event emitted when the rule changes
   */
  @Output() ruleChanged = new EventEmitter<GroupRule>();

  /**
   * Available rule types for child rules
   */
  ruleTypes = [
    { value: 'email', label: 'Email' },
    { value: 'userGroup', label: 'User Group' },
    { value: 'userRole', label: 'User Role' },
    { value: 'region', label: 'Region' },
    { value: 'lineOfBusiness', label: 'Line of Business' },
    { value: 'group', label: 'Group (Nested)' },
  ];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Ensure the rule form group has the necessary structure for a group rule
    this.ensureFormStructure();
  }

  /**
   * Ensure the form has the correct structure for group rules
   */
  private ensureFormStructure(): void {
    // Check if the rule control exists, if not create it
    if (!this.parentForm.get('rule')) {
      this.parentForm.addControl(
        'rule',
        this.fb.group({
          type: 'group',
          operator: 'AND',
          childRules: this.fb.array([]),
        })
      );
      return; // Structure is now fully set up
    }

    const ruleGroup = this.parentForm.get('rule') as FormGroup;

    if (!ruleGroup.contains('operator')) {
      ruleGroup.addControl('operator', this.fb.control('AND'));
    }

    if (!ruleGroup.contains('childRules')) {
      ruleGroup.addControl('childRules', this.fb.array([]));
    }

    // Ensure rule type is set to 'group'
    ruleGroup.patchValue({ type: 'group' }, { emitEvent: false });
  }

  /**
   * Get the child rules form array
   */
  get childRulesArray(): FormArray {
    return this.parentForm.get('rule.childRules') as FormArray;
  }

  /**
   * Get the operator value
   */
  get operator(): 'AND' | 'OR' {
    return (this.parentForm.get('rule.operator')?.value as 'AND' | 'OR') || 'AND';
  }

  /**
   * Set the operator value
   */
  setOperator(operator: 'AND' | 'OR'): void {
    const ruleGroup = this.parentForm.get('rule') as FormGroup;
    ruleGroup.patchValue({ operator });

    // Emit the updated rule
    this.emitUpdatedRule();
  }

  /**
   * Add a new child rule
   */
  addChildRule(type = 'boolean'): void {
    const newRule: any = { type };

    // Set appropriate defaults based on type
    switch (type) {
      case 'boolean':
        newRule.value = false;
        break;
      default:
        newRule.values = this.fb.array([]);
        break;
    }

    const childRule = this.fb.group(newRule);
    this.childRulesArray.push(childRule);

    // Emit the updated rule
    this.emitUpdatedRule();
  }

  /**
   * Remove a child rule
   */
  removeChildRule(index: number): void {
    this.childRulesArray.removeAt(index);

    // Emit the updated rule
    this.emitUpdatedRule();
  }

  /**
   * Get the rule type label
   */
  getRuleTypeLabel(type: string): string {
    const ruleType = this.ruleTypes.find(rt => rt.value === type);
    return ruleType ? ruleType.label : type;
  }

  /**
   * Handle child rule changes
   */
  onChildRuleChanged(index: number, rule: Rule): void {
    // Update the specified child rule with the full rule structure
    const childRuleGroup = this.childRulesArray.at(index) as FormGroup;

    // Clear existing controls if any and update with new rule structure
    Object.keys(rule).forEach(key => {
      if (!childRuleGroup.contains(key)) {
        if (key === 'values' && Array.isArray(rule[key])) {
          // Create FormArray with FormControls for each value
          const valuesArray = this.fb.array([]);
          rule[key].forEach(value => {
            valuesArray.push(this.fb.control(value));
          });
          childRuleGroup.addControl(key, valuesArray);
        } else {
          childRuleGroup.addControl(key, this.fb.control(rule[key]));
        }
      } else if (key === 'values' && Array.isArray(rule[key])) {
        // For existing values array, clear and add new controls
        const valuesArray = childRuleGroup.get(key) as FormArray;
        while (valuesArray.length) {
          valuesArray.removeAt(0);
        }
        rule[key].forEach(value => {
          valuesArray.push(this.fb.control(value));
        });
      } else {
        childRuleGroup.get(key).setValue(rule[key]);
      }
    });

    // Emit the updated group rule
    this.emitUpdatedRule();
  }

  /**
   * Get a FormGroup for a specific child rule at the given index
   * This creates a proper form structure for child components to use
   */
  getChildFormGroup(index: number): FormGroup {
    const childGroup = this.childRulesArray.at(index) as FormGroup;

    // Create a wrapper FormGroup that mimics the expected structure
    // Child components expect to find their form controls in parentForm.get('rule')
    const wrapperGroup = this.fb.group({});
    wrapperGroup.setControl('rule', childGroup);

    return wrapperGroup;
  }

  /**
   * Emit the updated rule
   */
  private emitUpdatedRule(): void {
    const updatedRule: GroupRule = {
      type: 'group',
      operator: this.operator,
      childRules: this.childRulesArray.value,
    };

    this.ruleChanged.emit(updatedRule);
  }
}
